import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
// Analytics moved to PublicRoutes - not needed on private/protected routes
import App from './App.jsx'
import { AuthProvider } from './lib/useSupabaseAuth'
import { fixedServiceWorkerManager } from './lib/fixedServiceWorkerUtils'
import { logger } from './lib/logger'
import './styles.css'

// Basic error handling - less aggressive filtering
window.addEventListener('error', (event) => {
	// Only filter out very specific third-party errors
	const errorMessage = event.error?.message || event.message || '';
	
	// Only skip the specific adoptableArticle error
	if (errorMessage.includes('adoptableArticle') && errorMessage.includes('textcontent')) {
		console.warn('[Filtered Error] Third-party error ignored:', errorMessage);
		return;
	}
	
	console.error('[Global Error]', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
	const reason = event.reason?.message || event.reason || '';

	// Only filter out the specific adoptableArticle error
	if (reason.includes('adoptableArticle') && reason.includes('textcontent')) {
		console.warn('[Filtered Rejection] Third-party rejection ignored:', reason);
		return;
	}

	// supabase-js wraps every auth call in a Web Lock (locks.js). When the
	// race-against-timeout in useSupabaseAuth drops the in-flight getSession,
	// the lock can later abort with AbortError — that's benign, not a crash.
	if (event.reason?.name === 'AbortError' || reason.includes('signal is aborted')) {
		console.warn('[Filtered Rejection] Auth lock abort ignored:', reason);
		event.preventDefault();
		return;
	}

	console.error('[Unhandled Promise Rejection]', event.reason);
});

// Mobile-optimized service worker registration
const registerServiceWorker = async () => {
	if ('serviceWorker' in navigator && !window.location.search.includes('no-sw')) {
		try {
			// Delay SW registration on mobile to prevent interference with React rendering
			const isMobile = window.innerWidth < 640
			const delay = isMobile ? 2000 : 1000
			
			return new Promise((resolve) => {
				setTimeout(async () => {
					try {
						if (!fixedServiceWorkerManager.registration) {
							await fixedServiceWorkerManager.register()
						}
						resolve()
					} catch (error) {
						console.error('[ServiceWorker] Registration failed:', error);
						resolve() // Resolve anyway to not block
					}
				}, delay)
			})
		} catch (error) {
			console.error('[ServiceWorker] Registration setup failed:', error);
			return Promise.resolve() // Return resolved promise to not block
		}
	}
	return Promise.resolve()
}

// Basic environment check
const checkEnvironment = () => {
	const hasSupabaseUrl = import.meta.env.VITE_SUPABASE_URL
	const hasSupabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
	
	if (!hasSupabaseUrl || !hasSupabaseKey) {
		return {
			error: true,
			message: 'Missing Supabase configuration'
		}
	}
	return { error: false }
}

// Mobile-optimized initialization
const initializeApp = () => {
	const container = document.getElementById('root')
	if (!container) {
		console.error('[Mobile Debug] Root container not found!')
		return
	}

	const isMobile = window.innerWidth < 640
	// Reduced mobile debug logging
	if (process.env.NODE_ENV === 'development' && Math.random() < 0.3) {
		logger.mobileDebug('Initializing app, isMobile:', isMobile)
	}

	// Add mobile-specific touch handling
	if (isMobile) {
		document.addEventListener('touchstart', () => {}, { passive: true })
	}

	// Ensure FontAwesome is loaded before rendering
	const ensureFontAwesomeLoaded = () => {
		return new Promise((resolve) => {
			if (window.fontAwesomeLoaded) {
				resolve()
				return
			}

			const checkFontAwesome = () => {
				const testElement = document.createElement('i')
				testElement.className = 'fas fa-test'
				testElement.style.position = 'absolute'
				testElement.style.left = '-9999px'
				document.body.appendChild(testElement)
				
				const computedStyle = window.getComputedStyle(testElement, '::before')
				const hasFontAwesome = computedStyle.fontFamily && computedStyle.fontFamily.includes('Font Awesome')
				
				document.body.removeChild(testElement)
				
				if (hasFontAwesome) {
					window.fontAwesomeLoaded = true
					resolve()
				} else {
					setTimeout(checkFontAwesome, 50)
				}
			}
			
			checkFontAwesome()
		})
	}

	// Wait for FontAwesome before rendering
	ensureFontAwesomeLoaded().then(() => {
		// Reduced mobile debug logging
		if (process.env.NODE_ENV === 'development' && Math.random() < 0.3) {
			logger.mobileDebug('FontAwesome loaded, proceeding with app initialization')
		}
		proceedWithInitialization(container, isMobile)
	})
}

const proceedWithInitialization = (container, isMobile) => {

	// Check environment
	const envCheck = checkEnvironment()
	if (envCheck.error) {
		console.error('[Mobile Debug] Environment check failed:', envCheck.message)
		showErrorScreen(container, 'Configuration Required', envCheck.message)
		return
	}

	// Public SEO routes that are pre-rendered at build time.
	// Authenticated/private routes are never pre-rendered — always use createRoot for them.
	const PUBLIC_ROUTE_PREFIXES = [
		'/', '/pricing', '/features', '/faqs', '/about',
		'/blog', '/prosperia-ai', '/privacy', '/eula', '/cookies',
	]
	const currentPath = window.location.pathname
	const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some(
		(p) => currentPath === p || (p !== '/' && currentPath.startsWith(p + '/'))
	)
	const isPrerendered = container.hasAttribute('data-prerendered') && isPublicRoute

	if (!isPrerendered) {
		// Show loading state
		showLoadingScreen(container, isMobile)
	}

	renderReactApp(container, isMobile, isPrerendered)
}

const showErrorScreen = (container, title, message) => {
	container.innerHTML = `
		<div style="
			min-height: 100vh;
			min-height: 100dvh;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			background: #12161c; /* pre-hydration flash bg — keep in sync with --semantic-bg-page dark */
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			padding: 1rem;
			margin: 0;
		">
			<div style="
				text-align: center;
				max-width: 400px;
				padding: 2rem;
				background: #181d24; /* --semantic-bg-surface dark */
				border-radius: 8px;
				box-shadow: 0 4px 6px rgba(0,0,0,0.2);
			">
				<h1 style="color: #e0685c; margin-bottom: 1rem;">${title}</h1> <!-- --semantic-accent-danger dark -->
				<p style="color: #9aa3ad; margin-bottom: 1rem;">${message}</p> <!-- --semantic-text-muted dark -->
				<button onclick="window.location.reload()" style="
					background: #4f8fe8; /* --semantic-accent-primary dark */
					color: white;
					border: none;
					padding: 0.5rem 1rem;
					border-radius: 4px;
					cursor: pointer;
				">Refresh</button>
			</div>
		</div>
	`
}

const showLoadingScreen = (container, isMobile) => {
	container.innerHTML = `
		<div style="
			min-height: 100vh;
			min-height: 100dvh;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			background: #12161c; /* pre-hydration flash bg — keep in sync with --semantic-bg-page dark */
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			padding: ${isMobile ? '1rem' : '0'};
			margin: 0;
		">
			<div style="text-align: center;">
				<div style="
					width: ${isMobile ? '32px' : '40px'};
					height: ${isMobile ? '32px' : '40px'};
					border: 4px solid #232932; /* --semantic-bg-elevated dark (spinner track) */
					border-top: 4px solid #4f8fe8; /* --semantic-accent-primary dark */
					border-radius: 50%;
					animation: spin 1s linear infinite;
					margin: 0 auto 1rem;
				"></div>
				<p style="color: #9aa3ad; font-size: ${isMobile ? '14px' : '16px'};">Loading...</p> <!-- --semantic-text-muted dark -->
				<style>
					@keyframes spin {
						0% { transform: rotate(0deg); }
						100% { transform: rotate(360deg); }
					}
				</style>
			</div>
		</div>
	`
}

const renderReactApp = (container, isMobile, isPrerendered = false) => {
	// Use requestAnimationFrame for better mobile performance
	requestAnimationFrame(() => {
		try {
			if (isPrerendered) {
				// Public SEO page: hydrate server-rendered HTML for best performance
				logger.mobileDebug('Hydrating pre-rendered public page...')
				hydrateRoot(container, <AppWrapper />)
				logger.mobileDebug('React app hydrated successfully')
			} else {
				// Authenticated/private route or non-pre-rendered page: fresh client render
				logger.mobileDebug('Creating React root...')
				const root = createRoot(container)
				root.render(<AppWrapper />)
				logger.mobileDebug('React app rendered successfully')
			}

			// Update last successful load timestamp
			localStorage.setItem('prosperia-last-update', Date.now().toString())

			// Verify the app actually rendered (skip for hydrated pages — content is already there)
			if (!isPrerendered) setTimeout(() => {
				// Check for React 18 root element or any content in the container
				const appContent = container.querySelector('#root > *') || container.children[0]
				if (!appContent || appContent.children.length === 0) {
					logger.warn('[Mobile Debug] React app may not have rendered properly, showing fallback...')
					showFallbackApp(container, isMobile)
				} else {
					logger.mobileDebug('React app verified as rendered')
					// Update timestamp again after verification
					localStorage.setItem('prosperia-last-update', Date.now().toString())
				}
			}, 2000) // Increased timeout to 2 seconds to allow for data loading

			// Register service worker after React is rendered
			registerServiceWorker().then(() => {
				// Force an update check after registration
				setTimeout(() => {
					fixedServiceWorkerManager.checkForUpdates().catch(err => {
						console.warn('[SW] Initial update check failed:', err)
					})
				}, 2000) // Wait 2 seconds after registration
			})

		} catch (error) {
			console.error('[Mobile Debug] React render failed:', error)
			showErrorScreen(container, 'Render Error', error.message)
		}
	})
}

const showFallbackApp = (container, isMobile) => {
	container.innerHTML = `
		<div style="
			min-height: 100vh;
			min-height: 100dvh;
			height: 100%;
			display: flex;
			flex-direction: column;
			background: #12161c; /* pre-hydration flash bg — keep in sync with --semantic-bg-page dark */
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			padding: ${isMobile ? '1rem' : '2rem'};
			margin: 0;
		">
			<div style="
				background: #181d24; /* --semantic-bg-surface dark */
				border-radius: 8px;
				box-shadow: 0 4px 6px rgba(0,0,0,0.2);
				padding: 2rem;
				margin-bottom: 1rem;
			">
				<h1 style="color: #cbd5e1; margin-bottom: 1rem; font-size: ${isMobile ? '20px' : '24px'};">Prosperia CRM</h1>
				<p style="color: #9aa3ad; margin-bottom: 1.5rem;">Professional Service Management Platform</p> <!-- --semantic-text-muted dark -->
				<div style="
					background: #78350f;
					border: 1px solid #f59e0b;
					border-radius: 4px;
					padding: 1rem;
					margin-bottom: 1.5rem;
				">
					<p style="color: #fde68a; margin: 0; font-size: 14px;">
						The app is loading. If this screen persists, please refresh the page.
					</p>
				</div>
				<button onclick="window.location.reload()" style="
					background: #4f8fe8; /* --semantic-accent-primary dark */
					color: white;
					border: none;
					padding: ${isMobile ? '0.75rem 1.5rem' : '0.5rem 1rem'};
					border-radius: 4px;
					cursor: pointer;
					font-size: ${isMobile ? '14px' : '16px'};
					width: 100%;
				">Refresh App</button>
			</div>
		</div>
	`
}

// Initialize the app
initializeApp()

// Create a simple wrapper component WITHOUT PWA startup handling
const AppWrapper = () => {
	logger.mobileDebug('AppWrapper component rendering...')
	return (
		<HelmetProvider>
			<BrowserRouter>
				<AuthProvider>
					<App />
				</AuthProvider>
			</BrowserRouter>
		</HelmetProvider>
	)
}

